import { create } from 'zustand'
import { focusManager } from '@tanstack/react-query'
import api, {
  setAccessToken,
  getAccessToken,
  getAccessTokenAgeMs,
  setAuthLoading,
  refreshAccessToken,
  ERR_REFRESH_AUTH_FAILED,
} from '../utils/api.js'
import { useSiteFilterStore } from './siteFilterStore.js'
import { useSettingsStore } from './settingsStore.js'
import { usePolicyStore } from './usePolicyStore.js'
import { Sentry } from '../lib/sentry.js'
import { analytics } from '../lib/posthog.js'
import { logError } from '../utils/logError.js'
import { clearAllWizardDrafts } from '../components/onboarding/production-wizard-steps/helpers.js'
import usePlansStore from './usePlansStore.js'
import { resetQueryCache } from '../lib/queryClient.js'
import { useMessengerStore } from './messengerStore.js'
import { clampDayStartHour } from '../lib/calendar-day-start.js'
import { setI18nOrgType } from '../i18n/index.jsx'
import type { User, Tenant } from '../types/domain'

/**
 * Login options accepted by `login()`. All optional — bare username/password
 * is the common path; the rest scope the attempt (admin gate, tenant slug,
 * persistent session).
 */
interface LoginOptions {
  tenantSlug?: string
  requireAdmin?: boolean
  rememberMe?: boolean
}

/**
 * Loose shape of the JSON payloads returned by the auth endpoints
 * (`/auth/login`, `/auth/me`, MFA flows, demo, impersonation). Endpoints
 * return overlapping subsets, so every field is optional. The index signature
 * preserves access to any field a `.jsx` consumer reads that isn't enumerated
 * here. Boundary type only — not the persisted domain shape.
 */
interface AuthResponse {
  access_token?: string
  token?: string
  user?: User
  tenant?: Tenant | null
  demo_expires_at?: string | null
  mfa_required?: boolean
  mfa_token?: string
  requireMfa?: boolean
  requireMfaEnrollment?: boolean
  mfaToken?: string
  mfaEnrollToken?: string
  recovery_codes?: string[]
  plan?: {
    name?: string
    features?: unknown
    modules?: unknown
  }
  [key: string]: unknown
}

/**
 * The auth store's full shape: session state plus every action. Action return
 * types are permissive (`unknown`/`Promise<unknown>`) where the underlying API
 * payload is loosely typed at the boundary; identifiers and entities use the
 * precise domain types. Runtime is unchanged — types only.
 */
export interface AuthState {
  // ── State ──────────────────────────────────────────────────
  user: User | null
  tenant: Tenant | null
  loading: boolean
  demoExpiresAt: string | null
  isImpersonating: boolean
  isAuthRefreshing: boolean
  simpleMode: boolean
  calendarDayStartHour: number

  // ── Actions ────────────────────────────────────────────────
  startDemo: () => Promise<unknown>
  switchDemoRole: (role: string) => Promise<unknown>
  login: (username: string, password: string, opts?: LoginOptions) => Promise<unknown>
  verifyMfa: (mfaToken: string, code: string, opts?: { tenantSlug?: string; rememberMe?: boolean }) => Promise<unknown>
  loginGlobalAdmin: (username: string, password: string) => Promise<unknown>
  setupGlobalMfa: (mfaEnrollToken: string) => Promise<unknown>
  verifyGlobalMfaSetup: (mfaEnrollToken: string, code: string) => Promise<unknown>
  verifyGlobalMfa: (mfaToken: string, code: string) => Promise<unknown>
  verifyGlobalMfaRecovery: (mfaToken: string, code: string) => Promise<unknown>
  impersonate: (tenantId: User['tenant_id']) => Promise<unknown>
  exitImpersonation: () => void
  logout: () => Promise<void>
  fetchMe: () => Promise<void>
  completeLogin: (data: { access_token?: string; user?: User; tenant?: Tenant | null }) => void
  updateUser: (userData: User) => void
  setSimpleMode: (val: unknown) => Promise<void>
  setCalendarDayStartHour: (val: unknown) => Promise<void>
  isAdmin: () => boolean
  isManager: () => boolean
  canEdit: () => boolean
  isGlobalAdmin: () => boolean
  roleLabel: () => string
  installFocusRefreshListener: () => () => void
}

// Read the cached calendar day-start hour for an instant first paint, before
// fetchMe rehydrates it from the user's server-side ui_preferences.
function cachedDayStartHour(): number {
  return clampDayStartHour(localStorage.getItem('sw-calendar-day-start'))
}

// Proactive-refresh tuning:
//   • PROACTIVE_REFRESH_AT_MS — refresh the access token if it's older than
//     this when the tab returns to focus. Set safely below the backend's 15min
//     JWT TTL so a query firing immediately after focus never races expiry.
//   • MIN_HIDDEN_MS_FOR_REFRESH — don't bother on a fast tab-flick (<60s).
//     Reduces refresh chatter for users who Cmd-Tab between work.
const PROACTIVE_REFRESH_AT_MS  = 12 * 60 * 1000
const MIN_HIDDEN_MS_FOR_REFRESH = 60 * 1000

function setSentryUser(user: User | null | undefined): void {
  if (user) {
    Sentry.setUser({ id: String(user.id), email: user.email, username: user.username })
  } else {
    Sentry.setUser(null)
  }
  setAnalyticsUser(user)
}

// PostHog identity, wired to the same login/refresh/logout choke point as
// Sentry. Consent + key gating lives in lib/posthog.js (pre-consent calls are
// silent no-ops), so this only decides WHO to identify, not WHETHER to send.
// Platform-admin and impersonation sessions are excluded so they don't
// pollute per-tenant product metrics — they reset() to an anonymous profile.
function setAnalyticsUser(user: User | null | undefined): void {
  if (!user) {
    analytics.reset()
    return
  }
  const isGlobalAdmin = user.is_global_admin === true || user.role === 'global_admin'
  if (isGlobalAdmin || _adminToken) {
    analytics.reset()
    return
  }
  analytics.identify(String(user.id), {
    email: user.email,
    username: user.username,
    role: user.role,
  })
  if (user.tenant_id != null) {
    analytics.group('tenant', String(user.tenant_id))
  }
}

// In-memory store for the admin token during impersonation.
// Using memory (not sessionStorage) prevents XSS from extracting the token.
let _adminToken: string | null | undefined = null
let _fetchMePromise: Promise<void> | null = null   // deduplicate concurrent fetchMe calls (React StrictMode)

// Module-scoped listener install state. We expose installFocusRefreshListener()
// as a store action but track install once at module scope so React StrictMode
// double-invocation in dev (mount → unmount → mount) doesn't attach two
// listeners. Returns an uninstall function for tests.
let _focusListenerInstalled = false
let _lastHiddenAt = 0

const useAuthStore = create<AuthState>()((set, get) => ({
  user:              null,
  tenant:            null,
  loading:           true,
  demoExpiresAt:     null,   // ISO string — set when in a temp demo session
  isImpersonating:   false,  // true while a platform admin is logged in as a tenant
  // True while a proactive refresh is in-flight after the tab returned to
  // focus. Queries that watch this flag should pause until the refresh
  // settles, preventing the parallel-401-storm-then-fail bug. See
  // useAuthGatedQueries / QueryClientGate for how the gate is consumed.
  isAuthRefreshing:  false,
  simpleMode:        localStorage.getItem('sw-simple-mode') === 'true',
  // Calendar Hub day/week axis start hour (0–23). Per-user preference,
  // persisted in users.ui_preferences.calendar_day_start_hour.
  calendarDayStartHour: cachedDayStartHour(),

  startDemo: async () => {
    const { data } = await api.post<AuthResponse>('/auth/demo-start')
    setAccessToken(data.access_token)
    // Set medium theme for demo
    localStorage.setItem('stagewise-theme', 'medium')
    document.documentElement.setAttribute('data-theme', 'medium')
    set({
      user: data.user,
      tenant: data.tenant || null,
      demoExpiresAt: data.demo_expires_at || null,
    })
    // Principal changed in-place — sync the analytics/Sentry identity and
    // drop any prior session's cached queries (F-053, silent-state run2).
    setSentryUser(data.user)
    resetQueryCache()
    useMessengerStore.getState().reset()
    return data
  },

  switchDemoRole: async (role) => {
    // Call the API first — if it rejects, do NOT touch auth state or settings.
    // Any corruption from a partial swap would leave the UI gated on the old
    // role's plan with the new role's JWT (or vice versa), which is exactly
    // the "switcher has no session effect" regression #21 we are fixing.
    let data: AuthResponse
    try {
      const res = await api.post<AuthResponse>('/demo/switch-role', { role })
      data = res.data
    } catch (err) {
      logError('demo.switchDemoRole', err)
      throw err
    }

    setAccessToken(data.access_token)
    set({ user: data.user, tenant: data.tenant || null })
    // Keep the analytics/Sentry identity in lock-step with the new role —
    // without this, events keep reporting the PRIOR principal (F-053).
    setSentryUser(data.user)

    // Mirror impersonate(): clear prior role's plan/module state so
    // AppLayout re-fetches /settings + /billing/status for the new role.
    // Without this, the sidebar and gated pages keep the previous role's
    // enabledModules cache and the switcher appears to "have no effect".
    const settings = useSettingsStore.getState()
    settings.resetSettings()
    usePolicyStore.getState().resetPolicy()
    // Wipe React Query cache so the new role never reads the prior role's
    // tenant/role-scoped data (Finding #1, in-place principal switch).
    resetQueryCache()
    useMessengerStore.getState().reset()

    // If the backend returned an authoritative plan + modules snapshot with
    // the new token, seed plan/module state directly to avoid a flash of stale
    // gating before AppLayout's follow-up /settings + /billing/status fetch.
    // NOTE: setSettingsLoaded(true) is intentionally omitted — mirroring
    // impersonate(), which also lets AppLayout run the full settings fetch
    // (currency, timezone, app_name, qa_mode, logo_url) rather than skipping
    // it. Plan gating seeds are fast-path only; non-plan settings re-fetch
    // via the normal AppLayout path.
    if (data.plan) {
      if (data.plan.name)     settings.setPlanName(data.plan.name)
      if (data.plan.features) settings.setPlanFeatures(data.plan.features)
      if (data.plan.modules)  settings.setEnabledModules(data.plan.modules)
    }

    return data
  },

  login: async (username, password, { tenantSlug, requireAdmin = false, rememberMe = false } = {}) => {
    const body = { username, password, require_admin: requireAdmin, remember_me: rememberMe, tenant_slug: tenantSlug }
    const { data } = await api.post<AuthResponse>('/auth/login', body)

    // MFA challenge: don't store tokens yet — return a special object
    if (data.mfa_required) {
      return { mfa_required: true, mfa_token: data.mfa_token, tenantSlug, rememberMe }
    }

    setAccessToken(data.access_token)
    set({ user: data.user, tenant: data.tenant || null })
    setSentryUser(data.user)
    return data.user
  },

  verifyMfa: async (mfaToken, code, { tenantSlug: _tenantSlug, rememberMe = false } = {}) => {
    const { data } = await api.post<AuthResponse>('/auth/mfa/verify', {
      mfa_token: mfaToken,
      code,
      remember_me: rememberMe,
    })
    setAccessToken(data.access_token)
    set({ user: data.user, tenant: data.tenant || null })
    setSentryUser(data.user)
    return data.user
  },

  loginGlobalAdmin: async (username, password) => {
    const { data } = await api.post<AuthResponse>('/auth/global-login', { username, password })
    if (data.requireMfaEnrollment) return { mfaEnrollment: { token: data.mfaEnrollToken, user: data.user } }
    if (data.requireMfa)           return { mfa: { token: data.mfaToken, user: data.user } }
    if (data.access_token) {
      setAccessToken(data.access_token)
      set({ user: data.user, tenant: null })
      setSentryUser(data.user)
      return { user: data.user }
    }
    throw new Error('Unexpected login response')
  },

  setupGlobalMfa: async (mfaEnrollToken) => {
    const { data } = await api.post<AuthResponse>('/auth/global-mfa/setup', { mfa_enroll_token: mfaEnrollToken })
    return data  // { secret, uri, qr_code }
  },

  verifyGlobalMfaSetup: async (mfaEnrollToken, code) => {
    const { data } = await api.post<AuthResponse>('/auth/global-mfa/verify-setup', { mfa_enroll_token: mfaEnrollToken, code })
    setAccessToken(data.access_token)
    set({ user: data.user, tenant: null })
    setSentryUser(data.user)
    return { user: data.user, recoveryCodes: data.recovery_codes }
  },

  verifyGlobalMfa: async (mfaToken, code) => {
    const { data } = await api.post<AuthResponse>('/auth/global-mfa/verify', { mfa_token: mfaToken, code })
    setAccessToken(data.access_token)
    set({ user: data.user, tenant: null })
    setSentryUser(data.user)
    return { user: data.user }
  },

  verifyGlobalMfaRecovery: async (mfaToken, code) => {
    const { data } = await api.post<AuthResponse>('/auth/global-mfa/recovery-verify', { mfa_token: mfaToken, code })
    setAccessToken(data.access_token)
    set({ user: data.user, tenant: null })
    setSentryUser(data.user)
    return { user: data.user }
  },

  impersonate: async (tenantId) => {
    _adminToken = getAccessToken()
    const { data } = await api.post<AuthResponse>(`/tenants/${tenantId}/impersonate`)
    setAccessToken(data.token || data.access_token)
    set({ user: data.user, tenant: data.tenant || null, isImpersonating: true })
    // Keep the analytics/Sentry identity in lock-step with the impersonated
    // principal — events must never keep the admin's identity (F-053).
    setSentryUser(data.user)
    // Clear prior tenant's plan/module state so AppLayout re-fetches for this tenant.
    useSettingsStore.getState().resetSettings()
    usePolicyStore.getState().resetPolicy()
    // Clear stale site filter and plans from the previous tenant's session.
    useSiteFilterStore.getState().clearFilter()
    usePlansStore.getState().resetLoaded()
    // Wipe React Query cache — entity-scoped keys are NOT tenant-namespaced, so
    // without this the impersonated tenant would briefly read the admin's (or a
    // prior tenant's) cached data. logout() is safe via full reload; this
    // in-place switch is not. (Finding #1, silent-state audit 2026-06-07.)
    resetQueryCache()
    useMessengerStore.getState().reset()
    return data
  },

  exitImpersonation: () => {
    const adminToken = _adminToken
    if (!adminToken && !get().isImpersonating) return
    _adminToken = null
    if (adminToken) {
      setAccessToken(adminToken)
    } else {
      // SET-1: a page refresh mid-impersonation drops the in-memory admin
      // token, but the admin's refresh cookie is untouched (impersonation
      // never rotates it). Drop the impersonation access token and let
      // fetchMe() restore the admin session via the cookie-refresh path —
      // if that fails the user simply lands on the login screen.
      setAccessToken(null)
    }
    set({ user: null, tenant: null, loading: true, isImpersonating: false })
    useSettingsStore.getState().resetSettings()
    usePolicyStore.getState().resetPolicy()
    // Clear stale site filter and plans from the impersonated tenant's session.
    useSiteFilterStore.getState().clearFilter()
    usePlansStore.getState().resetLoaded()
    // Wipe React Query cache so returning to the admin context never shows the
    // impersonated tenant's cached data. (Finding #1, silent-state audit.)
    resetQueryCache()
    useMessengerStore.getState().reset()
    get().fetchMe()
  },

  logout: async () => {
    const wasGlobalAdmin = get().user?.is_global_admin || get().user?.role === 'global_admin'
    const wasDemo = !!get().demoExpiresAt
    try { await api.post('/auth/logout') } catch (err) { logError('auth.logout', err) }
    setAccessToken(null)
    _adminToken = null
    set({ user: null, tenant: null, demoExpiresAt: null, isImpersonating: false })
    setSentryUser(null)
    useSettingsStore.getState().resetSettings()
    usePolicyStore.getState().resetPolicy()
    // Clear stale site filter and plans so the next session starts clean.
    useSiteFilterStore.getState().clearFilter()
    usePlansStore.getState().resetLoaded()
    // Sweep any in-progress wizard drafts so nothing leaks across accounts.
    clearAllWizardDrafts()
    window.location.href = wasDemo ? '/' : wasGlobalAdmin ? '/platform-login' : '/login'
  },

  fetchMe: () => {
    if (_fetchMePromise) return _fetchMePromise
    setAuthLoading(true)
    _fetchMePromise = (async () => {
      if (!getAccessToken()) {
        try {
          // cookie refresh attempt
          // Use the shared refresh promise so the 401 interceptor and fetchMe
          // never make competing refresh calls (which revoke each other's tokens).
          const data = await refreshAccessToken() as AuthResponse
          // refresh succeeded
          const refreshSimple = !!data.user?.ui_preferences?.simple_mode
          localStorage.setItem('sw-simple-mode', String(refreshSimple))
          const refreshDayStart = clampDayStartHour(data.user?.ui_preferences?.calendar_day_start_hour)
          localStorage.setItem('sw-calendar-day-start', String(refreshDayStart))
          const savedSiteFilter = data.user?.ui_preferences?.site_filter
          const siteFilterState = useSiteFilterStore.getState()
          if (savedSiteFilter) {
            if (!siteFilterState.siteIds.length) {
              siteFilterState.setSiteIds(String(savedSiteFilter).split(',').filter(Boolean))
            }
          } else if (!siteFilterState.siteIds.length && data.user?.site_id) {
            siteFilterState.setSiteIds([String(data.user.site_id)])
          }
          const savedRecents = data.user?.ui_preferences?.site_recents
          if (savedRecents && Array.isArray(savedRecents)) {
            siteFilterState.setRecents(savedRecents.map(String))
          }
          set({
            user: data.user,
            tenant: data.tenant || null,
            demoExpiresAt: data.demo_expires_at || null,
            simpleMode: refreshSimple,
            calendarDayStartHour: refreshDayStart,
            // SET-1: claim-derived. Impersonation tokens have no refresh
            // cookie, so this path normally restores the ADMIN session
            // (is_impersonating absent → false) — never a stale banner.
            isImpersonating: (data.user as { is_impersonating?: boolean } | undefined)?.is_impersonating === true,
            loading: false,
          })
          setSentryUser(data.user)
          return
        } catch (err) {
          const e = err as { response?: { status?: number; data?: { error?: string } }; message?: string }
          console.warn('[Auth] refresh failed:', e?.response?.status, e?.response?.data?.error || e?.message)
          set({ loading: false })
          return
        }
      }
      try {
        const { data } = await api.get<AuthResponse>('/auth/me')
        const meSimple = !!data.user?.ui_preferences?.simple_mode
        localStorage.setItem('sw-simple-mode', String(meSimple))
        const meDayStart = clampDayStartHour(data.user?.ui_preferences?.calendar_day_start_hour)
        localStorage.setItem('sw-calendar-day-start', String(meDayStart))
        const meSiteFilter = data.user?.ui_preferences?.site_filter
        const meSiteState = useSiteFilterStore.getState()
        if (meSiteFilter) {
          if (!meSiteState.siteIds.length) {
            meSiteState.setSiteIds(String(meSiteFilter).split(',').filter(Boolean))
          }
        } else if (!meSiteState.siteIds.length && data.user?.site_id) {
          meSiteState.setSiteIds([String(data.user.site_id)])
        }
        const meRecents = data.user?.ui_preferences?.site_recents
        if (meRecents && Array.isArray(meRecents)) {
          meSiteState.setRecents(meRecents.map(String))
        }
        // SET-1: derive impersonation state from the JWT claim surfaced by
        // /auth/me — the in-memory flag set by impersonate() does not survive
        // a page refresh, which previously dropped the banner while the admin
        // silently kept acting as the tenant.
        set({
          user: data.user,
          tenant: data.tenant || null,
          simpleMode: meSimple,
          calendarDayStartHour: meDayStart,
          isImpersonating: (data.user as { is_impersonating?: boolean } | undefined)?.is_impersonating === true,
          loading: false,
        })
        setSentryUser(data.user)
      } catch {
        setAccessToken(null)
        set({ user: null, tenant: null, loading: false })
      }
    })().finally(() => { _fetchMePromise = null; setAuthLoading(false) })
    return _fetchMePromise
  },

  completeLogin: (data) => {
    setAccessToken(data.access_token)
    set({ user: data.user, tenant: data.tenant || null, loading: false })
    setSentryUser(data.user)
  },

  updateUser: (userData) => set({ user: userData }),

  setSimpleMode: async (val) => {
    const enabled = !!val
    set({ simpleMode: enabled })
    localStorage.setItem('sw-simple-mode', String(enabled))
    try { await api.put('/users/me/ui-preferences', { simple_mode: enabled }) } catch (err) { logError('users.setSimpleMode', err) }
  },

  // Calendar day/week axis start hour. Optimistic store + localStorage update,
  // then persist to the user's server-side ui_preferences.
  setCalendarDayStartHour: async (val) => {
    const hour = clampDayStartHour(val)
    set({ calendarDayStartHour: hour })
    localStorage.setItem('sw-calendar-day-start', String(hour))
    try {
      await api.put('/users/me/ui-preferences', { calendar_day_start_hour: hour })
    } catch (err) { logError('users.setCalendarDayStartHour', err) }
  },

  isAdmin:       () => ['local_admin', 'global_admin'].includes(get().user?.role ?? ''),
  isManager:     () => ['local_admin', 'global_admin', 'manager'].includes(get().user?.role ?? ''),
  canEdit:       () => ['local_admin', 'global_admin', 'manager', 'staff'].includes(get().user?.role ?? ''),
  isGlobalAdmin: () => get().user?.is_global_admin === true || get().user?.role === 'global_admin',

  roleLabel: () => {
    const role = get().user?.role
    const labels: Record<string, string> = {
      local_admin:  'Admin',
      global_admin: 'Global Admin',
      manager:      'Manager',
      staff:        'Staff',
      viewer:       'Viewer',
    }
    return (role ? labels[role] : undefined) || role || 'User'
  },

  /**
   * Install a visibilitychange + focus listener that proactively refreshes
   * the access token when the user returns to a tab that's been hidden for
   * a while. This is the root-cause fix for the "leave tab open → return →
   * 'failed to load data'" bug:
   *
   *   1. Access tokens live in memory and expire after 15 minutes.
   *   2. React Query's refetchOnWindowFocus fires N parallel queries on
   *      return, each racing the now-expired token.
   *   3. The classic 401 → refresh → retry path works in theory but breaks
   *      on freshly-woken tabs where the network stack is slow (cold DNS,
   *      TCP slow-start) and the original AbortController times out the
   *      refresh itself, surfacing a misleading "failed to load data".
   *
   * The proactive listener handles the refresh ONCE, deliberately, BEFORE
   * any data fetches fire — eliminating the storm entirely. While the
   * refresh is in flight, `isAuthRefreshing` is true so query consumers
   * (via QueryClientGate / useAuthGate) can pause refetches.
   *
   * Idempotent: safe to call multiple times (StrictMode double-mount).
   * Returns an uninstall fn for tests.
   */
  installFocusRefreshListener: () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return () => {}
    if (_focusListenerInstalled) return () => {}
    _focusListenerInstalled = true

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        _lastHiddenAt = Date.now()
        return
      }
      // visibilityState === 'visible' — tab returned to foreground
      maybeProactiveRefresh()
    }

    const onFocus = () => { maybeProactiveRefresh() }

    function maybeProactiveRefresh() {
      // Skip during initial bootstrap — fetchMe() is already running and will
      // refresh the cookie itself; we'd just race it.
      if (get().loading) return
      // Skip on the login pages where we don't want a stray refresh attempt
      // to obscure the actual sign-in flow.
      const path = window.location.pathname || ''
      const isPublicPage = ['/', '/login', '/platform-login', '/signup', '/forgot-password', '/reset-password']
        .some(p => path === p || path.startsWith(p + '/'))
      if (isPublicPage) return
      // Skip rapid blur/focus flickers (Cmd-Tab between apps) — they don't
      // need a refresh and would just spam the backend.
      const hiddenFor = _lastHiddenAt ? Date.now() - _lastHiddenAt : 0
      if (hiddenFor < MIN_HIDDEN_MS_FOR_REFRESH) return
      // Skip if the token is fresh enough to comfortably outlast the next
      // few refetches — the 12min budget leaves a 3min safety buffer.
      const tokenAge = getAccessTokenAgeMs()
      if (getAccessToken() && tokenAge < PROACTIVE_REFRESH_AT_MS) return

      // Gate React Query's refetchOnWindowFocus refetches by telling its
      // focusManager the app is not focused yet. We restore focus AFTER the
      // refresh resolves — React Query will then refetch with a fresh token
      // in a single coordinated pass instead of racing the expired one.
      focusManager.setFocused(false)
      set({ isAuthRefreshing: true })
      refreshAccessToken()
        .catch(err => {
          // REFRESH_AUTH_FAILED → the api.js layer has already cleared the
          // token; let the next user action route to login as normal. Avoid
          // a hard navigate here so we don't yank the user mid-typing.
          // REFRESH_NETWORK_FAILED → quietly ignore; the next data fetch
          // will retry and QueryErrorState will surface the network state.
          if (err?.code !== ERR_REFRESH_AUTH_FAILED) {
            logError('auth.proactiveRefresh', err)
          }
        })
        .finally(() => {
          _lastHiddenAt = 0
          set({ isAuthRefreshing: false })
          // Re-arm React Query focus refetches. Using true (auto-detect)
          // lets the manager fall back to its default focus tracking once
          // the gate is released.
          focusManager.setFocused(true)
        })
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('focus', onFocus)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', onFocus)
      _focusListenerInstalled = false
      _lastHiddenAt = 0
    }
  },
}))

// Keep the i18n terminology overlay in sync with the signed-in tenant's
// vertical (Wave-5 G1). Subscribing here covers every set() path — login,
// refresh, /me, impersonation, logout — without touching each call site.
useAuthStore.subscribe((state, prev) => {
  const orgType = state.tenant?.org_type
  if (orgType !== prev?.tenant?.org_type) setI18nOrgType(orgType)
})

export default useAuthStore

// Test-only: reset module-scoped listener state. Public symbol kept under a
// recognizable name; production code never imports this.
export function __resetFocusListenerForTests() {
  _focusListenerInstalled = false
  _lastHiddenAt = 0
}
